import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Avatar,
  Dropdown,
  Table,
  Tag
} from 'antd';
import {
  UserOutlined,
  BookOutlined,
  ExperimentOutlined,
  PlayCircleOutlined,
  TeamOutlined,
  ReadOutlined,
  LogoutOutlined,
  HomeOutlined,
  PlusOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTrainings: 0,
    activeSessions: 0,
    totalLabs: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [usersRes, trainingsRes, sessionsRes, labsRes, activityRes] = await Promise.allSettled([
        api.get('/users'),
        api.get('/trainings'),
        api.get('/sessions/active'),
        api.get('/labs'),
        api.get('/reports/activity', { params: { limit: 10 } })
      ]);

      setStats({
        totalUsers: usersRes.status === 'fulfilled' ? usersRes.value.data.length : 0,
        totalTrainings: trainingsRes.status === 'fulfilled' ? trainingsRes.value.data.length : 0,
        activeSessions: sessionsRes.status === 'fulfilled' ? sessionsRes.value.data.length : 0,
        totalLabs: labsRes.status === 'fulfilled' ? labsRes.value.data.length : 0
      });

      if (activityRes.status === 'fulfilled') {
        setRecentActivity(activityRes.value.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/admin/dashboard'),
    },
    {
      key: 'profile',
      label: 'Profile',
      icon: <UserOutlined />,
      onClick: () => navigate('/admin/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const quickActions = [
    {
      title: 'Manage Users',
      icon: <TeamOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
      description: 'Create and manage user accounts',
      onClick: () => navigate('/admin/users'),
    },
    {
      title: 'Training Programs',
      icon: <ReadOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
      description: 'Create and configure trainings',
      onClick: () => navigate('/admin/trainings'),
    },
    {
      title: 'Manage Classrooms',
      icon: <TeamOutlined style={{ fontSize: '24px', color: '#1890ff' }} />,
      description: 'Create classrooms and enroll students',
      onClick: () => navigate('/admin/classrooms'),
    },
    {
      title: 'Lab Exercises',
      icon: <ExperimentOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
      description: 'Configure lab environments',
      onClick: () => navigate('/admin/trainings'), // Labs are accessed through trainings
    },
    {
      title: 'Student Assignments',
      icon: <UserOutlined style={{ fontSize: '24px', color: '#52c41a' }} />,
      description: 'Assign students to trainings',
      onClick: () => navigate('/admin/assignments'),
    },
    {
      title: 'Platform Settings',
      icon: <SettingOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
      description: 'System configuration',
      onClick: () => user?.role === 'super_admin' && navigate('/super-admin/system'),
      disabled: user?.role !== 'super_admin'
    },
  ];

  const activityColumns = [
    {
      title: 'User',
      dataIndex: 'user_name',
      key: 'user_name',
      width: '25%',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: '40%',
      render: (action) => {
        const colors = {
          user_created: 'green',
          user_updated: 'blue',
          training_created: 'cyan',
          lab_started: 'orange',
        };
        const color = colors[action] || 'default';
        return <Tag color={color}>{action.replace(/_/g, ' ').toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      width: '35%',
      ellipsis: true,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0, color: '#faad14' }}>
            USSEDU Platform
          </Title>
          <Tag color="orange" style={{ marginLeft: 16 }}>Admin</Tag>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>Welcome back, {user?.first_name}!</Title>
          <Text type="secondary">Manage your platform and monitor activity.</Text>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Users"
                value={stats.totalUsers}
                loading={loading}
                prefix={<UserOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Training Programs"
                value={stats.totalTrainings}
                loading={loading}
                prefix={<BookOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Active Sessions"
                value={stats.activeSessions}
                loading={loading}
                prefix={<PlayCircleOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Lab Exercises"
                value={stats.totalLabs}
                loading={loading}
                prefix={<ExperimentOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card title="Quick Actions" bordered={false}>
              <Row gutter={[16, 16]}>
                {quickActions.map((action, index) => (
                  <Col xs={24} sm={12} key={index}>
                    <Card
                      hoverable={!action.disabled}
                      onClick={action.disabled ? undefined : action.onClick}
                      style={{
                        textAlign: 'center',
                        opacity: action.disabled ? 0.5 : 1,
                        cursor: action.disabled ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Space direction="vertical" size="small">
                        {action.icon}
                        <Text strong>{action.title}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {action.description}
                        </Text>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card
              title="Recent Activity"
              bordered={false}
              extra={
                <Button
                  type="link"
                  onClick={() => user?.role === 'super_admin' && navigate('/super-admin/reports')}
                  disabled={user?.role !== 'super_admin'}
                >
                  View All
                </Button>
              }
            >
              <Table
                dataSource={recentActivity}
                columns={activityColumns}
                loading={loading}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ y: 300 }}
              />
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
}
